import React, { useEffect, useState, useRef } from "react";
import { Menu, Dropdown } from "semantic-ui-react";
import LogModal from "./LogModal";
import parsePath from "./parsePath";
import { viewLogs } from "./kibana";
import { overlayFrameProcs } from "./animation";
import { receiveLog } from "./protocol";
import { geoPath } from "d3";

const ItemMenu = ({
  x,
  y,
  visible,
  path,
  extraProps,
  close,
  command,
  setFocus,
  setFilter,
}) => {
  const menuElem = useRef();
  const [logModalInitialData, setLogModalInitialData] = useState({});

  useEffect(() => {
    if (visible && menuElem.current) {
      overlayFrameProcs.itemMenu = (overlay, bounds) => {
        const menu = menuElem.current;
        if (!menu) return;
        const menuRect = menu.getBoundingClientRect();
        const menuHeight = menuRect.height;
        const menuWidth = menuRect.width;
        const bw = bounds.x2 - bounds.x1 - 30;
        const bh = bounds.y2 - bounds.y1 - 30;
        let x = parseFloat(menu.style.left.replace("px", ""));
        let y = parseFloat(menu.style.top.replace("px", ""));
        if (x + menuWidth > bw) x = bw - menuWidth;
        if (y + menuHeight > bh) y = bh - menuHeight;
        menu.style.left = `${x}px`;
        menu.style.top = `${y}px`;
      };
      return () => {
        delete overlayFrameProcs["itemMenu"];
      };
    }
  }, [menuElem.current, visible]);

  useEffect(() => {
    if (visible) {
      const data = {};
      const route = parsePath(path);
      if (route == null) return; // e.g. for root
      for (let i = 0; i < route.length; i++) {
        const part = route[i];
        if ("objectType" in part) {
          const partName = part.objectRef.split("/").pop();
          switch (part.objectType.name) {
            case "app":
              data.useApp = true;
              data.app = partName;
              break;
            case "env":
              data.useEnv = true;
              data.env = partName;
              break;
            case "machine":
              data.useMachine = true;
              data.machine = partName;
              break;
            case "process":
              // nothing in kibana for this
              break;
            case "role":
              data.useRole = true;
              data.role = partName;
              break;
          }
        }
      }
      for (const key in extraProps) {
        data["use" + key[0].toUpperCase() + key.substring(1)] = true;
        data[key] = extraProps[key];
      }
      setLogModalInitialData(data);
    }
  }, [visible]);

  if (!visible) return null;

  const route = parsePath(path);
  if (route == null) return null; // e.g. for root

  let coll = null,
    obj = null;
  while (coll == null && obj == null && route.length > 0) {
    const part = route.pop();
    if ("objectType" in part) {
      obj = part;
    } else {
      coll = part;
    }
  }

  if (!obj) {
    // shouldn't happen..
    console.error("no object found for path on item menu open", path);
    return null;
  }

  let actions = [];
  let diagnostics = [
    {
      render: () => (
        <LogModal
          key={"logs"}
          as={Menu.Item}
          initialData={logModalInitialData}
          onSubmit={(d) => {
            close();
            viewLogs(d);
          }}
        >
          View kibana logs...
        </LogModal>
      ),
    },
    /*,
    {
      // for debugging...
      label: "Message audit",
      click: () => {
        const items = receiveLog.filter((l) => l.indexOf(path) != -1);
        items.forEach((x, i) => {
          console.log(i, x);
        });
        console.log(items.length + " log entries");
      },
    },*/
  ];

  switch (obj.objectType.name) {
    case "process":
      diagnostics.push({
        label: "Get thread info",
        click: () =>
          confirm(
            "This can cause a slight memory spike. If using Kubernetes/Docker and memory limits aren't high enough, the process might be killed.\n\nContinue?"
          ) && command("getthreadinfo " + path),
      });
      diagnostics.push({
        label: "Get memory info",
        click: () =>
          confirm(
            "This can cause a slight memory spike. If using Kubernetes/Docker and memory limits aren't high enough, the process might be killed.\n\nContinue?"
          ) && command("getmemoryinfo " + path),
      });
      actions.push({
        label: "Kill",
        click: () =>
          confirm("Really kill this process? -\n\n" + path) &&
          command("kill " + path),
      });
      actions.push({
        label: "Start SSH",
        click: () => command("start-ssh " + path),
      });
      break;
    case "scope":
      actions.push({
        label: "Cancel",
        click: () =>
          confirm("Really cancel this scope's execution? -\n\n" + path) &&
          command("cancel " + path),
      });
      break;
  }

  const parts = obj.objectRef.split("/");
  const objName = parts.pop();
  const objColl = parts.pop();

  return (
    <div ref={menuElem} style={{ position: "absolute", left: x, top: y }}>
      <Menu vertical>
        <Menu.Item header>{objName}</Menu.Item>
        <Menu.Item>
          <Menu.Header>View</Menu.Header>
          <Menu.Menu>
            <Menu.Item
              onClick={() => {
                setFocus(path, false);
                close();
              }}
            >
              Focus this
            </Menu.Item>
            <Menu.Item
              onClick={() => {
                let arr = path.split("/");
                if (arr.length < 3) {
                  setFocus("", true);
                  return;
                }
                arr.pop();
                arr.pop();
                setFocus(arr.join("/"), false);
                close();
              }}
            >
              Up one level
            </Menu.Item>
            <Menu.Item
              onClick={() => {
                setFocus(path, true);
                close();
              }}
            >
              Focus this (include parents)
            </Menu.Item>
            <Menu.Item
              onClick={() => {
                setFilter(objColl, objName);
                close();
              }}
            >
              Filter for{" "}
              {objColl.substring(0, objColl.length - 1) + " = " + objName}
            </Menu.Item>
            <Menu.Item
              onClick={() => {
                setFocus("", true);
                setFilter(null, null);
                close();
              }}
            >
              Reset
            </Menu.Item>
          </Menu.Menu>
        </Menu.Item>
        {actions.length == 0 ? null : (
          <Menu.Item>
            <Menu.Header>Actions</Menu.Header>
            <Menu.Menu>
              {actions.map((action) =>
                action.render ? (
                  action.render()
                ) : (
                  <Menu.Item
                    key={action.label}
                    onClick={() => {
                      close();
                      action.click();
                    }}
                  >
                    {action.label}
                  </Menu.Item>
                )
              )}
            </Menu.Menu>
          </Menu.Item>
        )}
        <Menu.Item>
          <Menu.Header>Diagnostics</Menu.Header>
          <Menu.Menu>
            {diagnostics.map((action) =>
              action.render ? (
                action.render()
              ) : (
                <Menu.Item
                  key={action.label}
                  onClick={() => {
                    close();
                    action.click();
                  }}
                >
                  {action.label}
                </Menu.Item>
              )
            )}
          </Menu.Menu>
        </Menu.Item>
      </Menu>
    </div>
  );
};

export default ItemMenu;
